'use client';

import React, { useState, useCallback } from 'react';
import { Box, Typography, Grid, Card, CardContent, CardActions, Button, Dialog, DialogTitle, DialogContent, TextField, Stack, Paper, IconButton } from '@mui/material';
import Link from 'next/link';
import { Icon } from '@iconify/react';
import ArrowBackIcon from '@/letsgo/icons/ArrowBack';
import { getToolIconForPath } from '@/lib/toolsData';
import { getTemplates } from '@/lib/templates';
import { downloadCSV, downloadICS } from '@/lib/templates/export';
import { useAiActionCooldown } from '@/hooks/useAiActionCooldown';

const CATEGORIES = ['weekly', 'campaign', 'evergreen', 'holiday'] as const;
const SEGMENT_FILTERS = ['all', 'builtin', 'mine', 'public'] as const;

export default function CalendarTemplatesPage() {
  const allTemplates = getTemplates();
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const templates = categoryFilter === 'all'
    ? allTemplates
    : allTemplates.filter((t: any) => t.category === categoryFilter);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [variables, setVariables] = useState<Record<string,string>>({});
  const [date, setDate] = useState<string>(new Date().toISOString().slice(0,10));
  const [shareId, setShareId] = useState<string>('');
  const [genOpen, setGenOpen] = useState(false);
  const [genBrief, setGenBrief] = useState('');
  const [genPlatforms, setGenPlatforms] = useState('instagram,x');
  const [genDays, setGenDays] = useState(7);
  const [genTone, setGenTone] = useState('informal');
  const [genResult, setGenResult] = useState<any[] | null>(null);
  const [segmentFilter, setSegmentFilter] = useState<'all' | 'builtin' | 'mine' | 'public'>('all');
  const [variantOpen, setVariantOpen] = useState(false);
  const [variants, setVariants] = useState<any[] | null>(null);
  const [variantIndex, setVariantIndex] = useState<number | null>(null);
  const [abOpen, setAbOpen] = useState(false);
  const [abSelection, setAbSelection] = useState<{a?:string,b?:string}>({});
  const [abScheduleA, setAbScheduleA] = useState<string>(new Date().toISOString().slice(0,10));
  const [abScheduleB, setAbScheduleB] = useState<string>(new Date(Date.now()+24*3600*1000).toISOString().slice(0,10));
  const [fbOpen, setFbOpen] = useState(false);
  const [fbAbId, setFbAbId] = useState('');
  const [fbVariant, setFbVariant] = useState('A');
  const [fbOutcome, setFbOutcome] = useState('worked');
  const [fbNotes, setFbNotes] = useState('');
  const { run: runCalendarAi, disabled: calendarAiDisabled } = useAiActionCooldown(4000);

  const handleAiGenerateCampaign = useCallback(() => {
    void runCalendarAi(async () => {
      try {
        const res = await fetch('/api/tools/calendar-templates/ai/generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            brief: genBrief,
            platforms: genPlatforms.split(',').map((s) => s.trim()),
            days: genDays,
            tone: genTone,
          }),
        });
        const j = await res.json();
        if (j.ok) {
          setGenResult(j.calendar);
        } else {
          alert('Generate failed: ' + (j.error || 'unknown'));
        }
      } catch (e) {
        console.error(e);
        alert('Generate error');
      }
    });
  }, [runCalendarAi, genBrief, genPlatforms, genDays, genTone]);

  const handleAiVariantsForItem = useCallback(
    (it: { caption: string; platform: string }, idx: number) => {
      void runCalendarAi(async () => {
        try {
          const res = await fetch('/api/tools/calendar-templates/ai/variants', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ caption: it.caption, platform: it.platform, tone: genTone, max_variants: 4 }),
          });
          const j = await res.json();
          if (j.ok) {
            setVariants(j.variants);
            setVariantIndex(idx);
            setVariantOpen(true);
          } else {
            alert('Variants failed');
          }
        } catch (e) {
          console.error(e);
          alert('Variants error');
        }
      });
    },
    [runCalendarAi, genTone]
  );

  const openTemplate = (t:any) => {
    setSelected(t);
    setVariables({});
    setDate(new Date().toISOString().slice(0,10));
    setOpen(true);
  };

  const renderFilled = (tpl:string) => {
    let out = tpl;
    Object.keys(variables).forEach(k => { out = out.replace(new RegExp(`{{\\s*${k}\\s*}}`, 'g'), variables[k]); });
    out = out.replace(/{{\s*date\s*}}/g, date);
    return out;
  };

  const buildItems = () => {
    if (!selected) return [];
    const items:any[] = [];
    selected.platforms.forEach((p:any) => {
      items.push({ date, platform: p.platform, caption: renderFilled(p.captionTemplate) });
    });
    return items;
  };

  const onExportCSV = () => {
    const items = buildItems();
    downloadCSV(items);
  };
  const onExportICS = () => {
    const items = buildItems();
    downloadICS(items);
  };

  const saveCampaign = async () => {
    const items = buildItems();
    const payload = { name: selected.title, items };
    try {
      const res = await fetch('/api/tools/calendar-templates/save', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const j = await res.json();
      if (j.ok && j.id) setShareId(j.id);
    } catch (e) { console.error(e); }
  };

  return (
    <Box component="main" id="main-content" sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton component={Link} href="/tools" aria-label="Back to Tools"><ArrowBackIcon /></IconButton>
          <Icon icon={getToolIconForPath('/tools/calendar-templates')} width={34} height={34} />
          <Typography variant="h4">Calendar Templates</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: (t) => t.palette.mode === 'dark' ? 'grey.800' : 'grey.100', color: (t) => t.palette.mode === 'dark' ? 'grey.200' : 'text.secondary' }}>
            <Typography variant="caption" color="inherit">Export-first</Typography>
          </Box>
          <Link href="/api/tools/calendar-templates/oauth/google/start" style={{ textDecoration: 'none' }}>
            <Button component="span" size="small">Connect Accounts</Button>
          </Link>
        </Box>

        {/* Segment filter row (Show: All / Built-in / My templates / Public) */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ flexShrink: 0 }}>Show:</Typography>
          {SEGMENT_FILTERS.map((seg) => (
            <Button
              key={seg}
              size="small"
              variant={segmentFilter === seg ? 'contained' : 'outlined'}
              onClick={() => setSegmentFilter(seg)}
              sx={{ minWidth: 64 }}
            >
              {seg === 'all' ? 'All' : seg === 'builtin' ? 'Built-in' : seg === 'mine' ? 'My templates' : 'Public'}
            </Button>
          ))}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Category:</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start', rowGap: 1 }}>
            <Button size="small" variant={categoryFilter === 'all' ? 'contained' : 'outlined'} onClick={()=>setCategoryFilter('all')} sx={{ minWidth: 64, textTransform: 'capitalize', px: 1, ml: 0 }}>All</Button>
            {CATEGORIES.map((cat)=> (
              <Button
                key={cat}
                size="small"
                variant={categoryFilter === cat ? 'contained' : 'outlined'}
                onClick={()=>setCategoryFilter(cat)}
                sx={{ minWidth: 64, textTransform: 'capitalize', px: 1, ml: 0 }}
              >
                {cat}
              </Button>
            ))}
          </Box>
        </Box>
        <Grid container spacing={2}>
          {templates.map((t:any)=> (
            <Grid item xs={12} sm={6} md={4} key={t.id}>
              <Card>
                <CardContent>
                  <Typography variant="caption" color="text.secondary" textTransform="capitalize">{t.category}</Typography>
                  <Typography variant="h6">{t.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{t.description}</Typography>
                </CardContent>
                <CardActions>
                  <Button onClick={()=>openTemplate(t)}>Use Template</Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">This tool is export-first by default — you can export CSV/ICS or save shareable campaigns without connecting accounts. Connect a scheduler or Google Calendar to enable direct publish.</Typography>
        </Box>
        <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-start', alignItems: 'center' }}>
          <Button variant="outlined" onClick={()=>setGenOpen(true)}>Generate from brief (AI)</Button>
          <Button variant="outlined" onClick={()=>setFbOpen(true)}>Submit A/B Feedback</Button>
        </Box>

        <Dialog open={open} onClose={()=>setOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Use Template: {selected?.title}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Date" type="date" value={date} onChange={(e)=>setDate(e.target.value)} />
              <TextField label="Variables (JSON)" multiline minRows={2} value={JSON.stringify(variables)} onChange={(e)=>{ try { setVariables(JSON.parse(e.target.value)); } catch { /* ignore parse errors while typing */ } }} />

              <Box>
                <Typography variant="subtitle1">Preview</Typography>
                {selected?.platforms?.map((p:any,idx:number)=> (
                  <Box key={idx} sx={{ p:1, border: '1px solid rgba(0,0,0,0.06)', mb:1 }}>
                    <Typography variant="caption">{p.platform}</Typography>
                    <Typography variant="body2">{renderFilled(p.captionTemplate)}</Typography>
                    <Button size="small" sx={{ mt:0.5 }} onClick={()=>{ navigator.clipboard?.writeText(renderFilled(p.captionTemplate)); }}>Copy caption</Button>
                  </Box>
                ))}
              </Box>

              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={onExportCSV}>Export CSV</Button>
                <Button variant="outlined" onClick={onExportICS}>Export ICS</Button>
                <Button onClick={saveCampaign}>Save & Share</Button>
                <Button onClick={()=>{ setOpen(false); setShareId(''); }}>Close</Button>
              </Stack>

              {shareId && (
                <Box>
                  <Typography>Saved. Share ID: {shareId}</Typography>
                  <Link href={`/tools/calendar-templates/share?id=${shareId}`}><a>Open Shared Campaign</a></Link>
                </Box>
              )}
            </Stack>
          </DialogContent>
        </Dialog>

        <Dialog open={genOpen} onClose={()=>{ setGenOpen(false); setGenResult(null); }} maxWidth="md" fullWidth>
          <DialogTitle>Generate Campaign from Brief</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Brief" multiline minRows={4} value={genBrief} onChange={(e)=>setGenBrief(e.target.value)} />
              <TextField label="Platforms (csv)" value={genPlatforms} onChange={(e)=>setGenPlatforms(e.target.value)} />
              <TextField label="Days" type="number" value={genDays} onChange={(e)=>setGenDays(Number(e.target.value))} />
              <TextField label="Tone" value={genTone} onChange={(e)=>setGenTone(e.target.value)} />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={handleAiGenerateCampaign} disabled={calendarAiDisabled}>
                  Generate
                </Button>
                <Button onClick={()=>{ setGenOpen(false); setGenResult(null); }}>Close</Button>
              </Stack>

              {genResult && (
                <Box>
                  <Typography variant="h6">Generated Items</Typography>
                  {genResult.map((it:any, idx:number)=> (
                    <Paper key={idx} sx={{ p:1, mt:1 }}>
                      <Typography variant="subtitle2">{it.date} — {it.platform}</Typography>
                      <Typography variant="body2">{it.caption}</Typography>
                      <Typography variant="caption">Hashtags: {Array.isArray(it.suggested_hashtags) ? it.suggested_hashtags.join(', ') : ''}</Typography>
                      {it.estimate && (<Typography variant="caption" display="block">Estimate: {JSON.stringify(it.estimate)}</Typography>)}
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button size="small" onClick={() => handleAiVariantsForItem(it, idx)} disabled={calendarAiDisabled}>
                          Variants
                        </Button>
                        <Button size="small" onClick={()=>{ setAbOpen(true); setVariantIndex(idx); }}>A/B test</Button>
                      </Stack>
                    </Paper>
                  ))}
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button variant="contained" onClick={async ()=>{
                      try {
                        const payload = { name: 'AI Campaign', items: genResult };
                        const res = await fetch('/api/tools/calendar-templates/save', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
                        const j = await res.json();
                        if (j.ok && j.id) { setShareId(j.id); alert('Saved campaign: ' + j.id); }
                      } catch (e) { console.error(e); }
                    }}>Save as Campaign</Button>
                    <Button onClick={()=>{ /* TODO: import into editor flow */ alert('You can now open editor and load the saved draft.'); }}>Open in Editor</Button>
                  </Stack>
                </Box>
              )}
              {/* annotate after generation */}
              <script dangerouslySetInnerHTML={{ __html: `
                (function(){
                  try{
                    const items = ${JSON.stringify(genResult || [])};
                    if(!items || items.length===0) return;
                    fetch('/api/tools/calendar-templates/ai/annotate', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) }).then(r=>r.json()).then(j=>{ if(j.ok){ /* naive: reload page to show */ location.reload(); } }).catch(()=>{});
                  }catch(e){}
                })();
              ` }} />
            </Stack>
          </DialogContent>
        </Dialog>

        <Dialog open={variantOpen} onClose={()=>{ setVariantOpen(false); setVariants(null); }}>
          <DialogTitle>Variants</DialogTitle>
          <DialogContent>
            {variants ? variants.map((v:any, i:number)=> (
              <Paper key={i} sx={{ p:1, mt:1 }}>
                <Typography variant="body2">{v.caption}</Typography>
                <Typography variant="caption">{Array.isArray(v.hashtags) ? v.hashtags.join(', ') : ''}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt:1 }}>
                  <Button size="small" onClick={()=>{
                    // replace caption in genResult
                    const g = [...(genResult||[])];
                    if (variantIndex !== null) { g[variantIndex].caption = v.caption; setGenResult(g); }
                    setVariantOpen(false);
                  }}>Use Variant</Button>
                  <Button size="small" onClick={()=>{ navigator.clipboard?.writeText(v.caption); alert('Copied'); }}>Copy</Button>
                </Stack>
              </Paper>
            )) : <Typography>Loading...</Typography>}
          </DialogContent>
        </Dialog>

        <Dialog open={abOpen} onClose={()=>setAbOpen(false)}>
          <DialogTitle>Run A/B Test</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt:1 }}>
              <Typography>Variant A (pick or paste)</Typography>
              <TextField value={abSelection.a||''} onChange={(e)=>setAbSelection(s=>({ ...s, a: e.target.value }))} />
              <Typography>Variant B (pick or paste)</Typography>
              <TextField value={abSelection.b||''} onChange={(e)=>setAbSelection(s=>({ ...s, b: e.target.value }))} />
              <TextField label="Schedule A" type="date" value={abScheduleA} onChange={(e)=>setAbScheduleA(e.target.value)} />
              <TextField label="Schedule B" type="date" value={abScheduleB} onChange={(e)=>setAbScheduleB(e.target.value)} />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={async ()=>{
                  try {
                    if (variantIndex === null) return alert('No item selected');
                    if (!genResult) return alert('No generated items available');
                    const base = genResult[variantIndex];
                    if (!abSelection.a || !abSelection.b) return alert('Provide both variants');
                    const payload = { baseItem: base, variantA: abSelection.a, variantB: abSelection.b, scheduleA: abScheduleA, scheduleB: abScheduleB };
                    const res = await fetch('/api/tools/calendar-templates/ab/create', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
                    const j = await res.json();
                    if (j.ok) { alert('A/B test created: ' + j.abId); setAbOpen(false); }
                    else alert('AB create failed');
                  } catch(e){console.error(e); alert('AB error');}
                }}>Create A/B Test</Button>
                <Button onClick={()=>setAbOpen(false)}>Cancel</Button>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>
        <Dialog open={fbOpen} onClose={()=>setFbOpen(false)}>
          <DialogTitle>Submit A/B Feedback</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt:1 }}>
              <TextField label="A/B Test ID" value={fbAbId} onChange={(e)=>setFbAbId(e.target.value)} />
              <TextField label="Variant (A/B)" value={fbVariant} onChange={(e)=>setFbVariant(e.target.value)} />
              <TextField label="Outcome (worked/didnt)" value={fbOutcome} onChange={(e)=>setFbOutcome(e.target.value)} />
              <TextField label="Notes" multiline minRows={2} value={fbNotes} onChange={(e)=>setFbNotes(e.target.value)} />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={async ()=>{
                  try {
                    const res = await fetch('/api/tools/calendar-templates/feedback', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ abId: fbAbId, variant: fbVariant, outcome: fbOutcome, notes: fbNotes }) });
                    const j = await res.json();
                    if (j.ok) { alert('Feedback submitted'); setFbOpen(false); }
                    else alert('Feedback failed');
                  } catch (e) { console.error(e); alert('Feedback error'); }
                }}>Submit</Button>
                <Button onClick={()=>setFbOpen(false)}>Cancel</Button>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>
    </Box>
  );
}
